import React, { useState } from 'react';
import {
  Camera,
  Wand2,
  Link as LinkIcon,
  Check,
  Copy,
  Send,
  Info,
  User,
  Briefcase,
  Mail,
  Calendar,
  MapPin,
  AlignLeft,
  Tag,
} from 'lucide-react';
import type {
  ParticipantRole,
  PhotographerData,
  ReleaseInput,
  ServerConfig,
  ShootDetails,
} from '@shared/types';
import { adminApi } from './api';
import { useLang } from '../i18n';

interface CreateReleaseProps {
  aiEnabled: boolean;
  defaults: ServerConfig['defaults'];
}

const ROLE_KEYS: { id: ParticipantRole; tKey: 'role.model' | 'role.makeup_artist' | 'role.stylist' | 'role.retoucher' | 'role.assistant' | 'role.other' }[] = [
  { id: 'model', tKey: 'role.model' },
  { id: 'makeup_artist', tKey: 'role.makeup_artist' },
  { id: 'stylist', tKey: 'role.stylist' },
  { id: 'retoucher', tKey: 'role.retoucher' },
  { id: 'assistant', tKey: 'role.assistant' },
  { id: 'other', tKey: 'role.other' },
];

const CreateRelease: React.FC<CreateReleaseProps> = ({ aiEnabled, defaults }) => {
  const { t } = useLang();
  const [photographer, setPhotographer] = useState<PhotographerData>({
    name: defaults.photographerName,
    email: defaults.photographerEmail,
    company: '',
  });
  const [shoot, setShoot] = useState<ShootDetails>({
    title: defaults.shootTitle,
    date: new Date().toISOString().split('T')[0],
    location: '',
    description: '',
    isCommercial: true,
  });
  const [role, setRole] = useState<ParticipantRole>('model');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sentToChat, setSentToChat] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setToken(null);
    setSignUrl(null);
    setSentToChat(false);
    setCopied(false);
    setErr(null);
  };

  const handleImproveDescription = async () => {
    if (!shoot.description) return;
    setIsGeneratingAi(true);
    try {
      const { text } = await adminApi.refine(shoot.description, shoot.isCommercial);
      setShoot((prev) => ({ ...prev, description: text }));
    } catch (e) {
      console.error(e);
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handleCreate = async () => {
    if (!photographer.name || !photographer.email || !shoot.title) {
      setErr(t('create.error.requiredFields'));
      return;
    }
    setErr(null);
    setCreating(true);
    try {
      const input: ReleaseInput = { photographer, shoot, role };
      const res = await adminApi.createRelease(input);
      setToken(res.token);
      setSignUrl(res.signUrl);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('create.error.generic'));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = () => {
    if (!signUrl) return;
    navigator.clipboard.writeText(signUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendToMe = async () => {
    if (!token) return;
    try {
      const res = await adminApi.resendLink(token);
      setSentToChat(res.sentToChat);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось отправить');
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24">
      <header className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200 mb-3">
          <Camera size={22} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 serif">{t('create.heading')}</h1>
      </header>

      <div className="space-y-5 bg-white p-5 rounded-2xl shadow-sm border border-gray-200">
        <Section title={t('create.section.photographer')}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field
              icon={<User size={14} />}
              label={t('create.field.name')}
              value={photographer.name}
              onChange={(v) => setPhotographer({ ...photographer, name: v })}
              placeholder={t('create.placeholder.name')}
            />
            <Field
              icon={<Briefcase size={14} />}
              label={`${t('create.field.company')} (${t('common.optional')})`}
              value={photographer.company ?? ''}
              onChange={(v) => setPhotographer({ ...photographer, company: v })}
            />
            <div className="md:col-span-2">
              <Field
                icon={<Mail size={14} />}
                label={t('create.field.email')}
                value={photographer.email}
                onChange={(v) => setPhotographer({ ...photographer, email: v })}
                placeholder={t('create.placeholder.email')}
                type="email"
              />
            </div>
          </div>
        </Section>

        <Section title={t('create.section.shoot')}>
          <Field
            icon={<Tag size={14} />}
            label={t('create.field.title')}
            value={shoot.title}
            onChange={(v) => setShoot({ ...shoot, title: v })}
            placeholder={t('create.placeholder.title')}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              icon={<Calendar size={14} />}
              label={t('create.field.date')}
              value={shoot.date}
              onChange={(v) => setShoot({ ...shoot, date: v })}
              type="date"
            />
            <Field
              icon={<MapPin size={14} />}
              label={t('create.field.location')}
              value={shoot.location}
              onChange={(v) => setShoot({ ...shoot, location: v })}
              placeholder={t('create.placeholder.location')}
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                <AlignLeft size={14} className="text-gray-400" /> {t('create.field.description')}
              </label>
              {aiEnabled && (
                <button
                  onClick={handleImproveDescription}
                  disabled={isGeneratingAi || !shoot.description}
                  className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                >
                  <Wand2 size={11} />
                  {isGeneratingAi ? t('create.aiImproving') : t('create.aiImprove')}
                </button>
              )}
            </div>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition h-20 resize-none"
              value={shoot.description}
              onChange={(e) => setShoot({ ...shoot, description: e.target.value })}
              placeholder={t('create.placeholder.description')}
            />
          </div>
          <label className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 transition p-3 rounded-lg cursor-pointer select-none">
            <input
              type="checkbox"
              checked={shoot.isCommercial}
              onChange={(e) => setShoot({ ...shoot, isCommercial: e.target.checked })}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300"
            />
            <span className="text-sm text-gray-800">{t('create.field.commercial')}</span>
          </label>
        </Section>

        <Section title={t('create.section.role')}>
          <div className="grid grid-cols-2 gap-2">
            {ROLE_KEYS.map((r) => (
              <button
                key={r.id}
                onClick={() => setRole(r.id)}
                className={`relative p-2.5 rounded-xl text-sm font-medium transition border-2 ${
                  role === r.id
                    ? 'border-indigo-600 bg-indigo-600 text-white shadow-md shadow-indigo-200'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-200 hover:text-gray-900'
                }`}
              >
                {t(r.tKey)}
                {role === r.id && (
                  <Check size={14} className="absolute top-1.5 right-1.5 text-white" />
                )}
              </button>
            ))}
          </div>
        </Section>

        <div className="pt-2">
          {!signUrl ? (
            <>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3.5 rounded-xl font-semibold hover:opacity-95 disabled:from-gray-300 disabled:to-gray-300 transition flex justify-center items-center gap-2 shadow-md shadow-indigo-200"
              >
                <LinkIcon size={16} />
                {creating ? t('create.submitting') : t('create.submit')}
              </button>
              {err && (
                <p className="text-sm text-red-600 mt-2 px-1">{err}</p>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-800 text-sm font-semibold mb-2 flex items-center gap-2">
                  <Check size={16} /> {t('create.linkReady')}
                </p>
                <input
                  type="text"
                  readOnly
                  value={signUrl}
                  className="w-full text-xs p-2 border border-green-200 rounded bg-white text-gray-700 truncate font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={copyLink}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? t('common.copied') : t('create.copy')}
                </button>
                <button
                  onClick={sendToMe}
                  disabled={sentToChat}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg text-sm font-medium disabled:bg-gray-300 transition flex items-center justify-center gap-2"
                >
                  <Send size={14} />
                  {sentToChat ? t('create.sentToTelegram') : t('create.sendToTelegram')}
                </button>
              </div>
              <button
                onClick={reset}
                className="w-full text-xs text-gray-500 hover:text-gray-700 underline mt-2"
              >
                {t('create.another')}
              </button>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex gap-2 items-start text-xs text-blue-800">
                <Info size={14} className="mt-0.5 shrink-0" />
                <p>{t('create.tip')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100 pb-2">
      {title}
    </h2>
    {children}
  </section>
);

interface FieldProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}

const Field: React.FC<FieldProps> = ({ icon, label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
      {icon && <span className="text-gray-400">{icon}</span>}
      {label}
    </label>
    <input
      type={type}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

export default CreateRelease;
